import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendNotification } from "../../features/notificationSlice/notificationSlice";
import { showModal } from "../../features/statusModal/statusModal";
import Dialog from "../dialog/Dialog";
import DialogButton from "../dialog/DialogButton";
import NotificationIcon from "../icons/NotificationIcon";
import TextArea from "../inputs/textArea/TextArea";
import TextInput from "../inputs/textInput/TextInput";
import TextWrapper from "../textWrapper/TextWrapper";
import styles from "./sentNotification.module.css";
import SelectInput from "../inputs/selectInput/SelectInput";
import { fetchPaginatedTournament } from "../../features/tournaments/tournamentsSlice";

const DEFAULT_BEHAVIOR_OPTIONS = [
  { value: "general", label: "عامة" },
  { value: "scroll_to_tournament", label: "الانتقال الى البطولة" },
  { value: "open_link", label: "فتح الرابط" },
  { value: "room_information", label: "ارسال معلومات الروم" },
];

const ALL_TARGET_BEHAVIOR_OPTIONS = DEFAULT_BEHAVIOR_OPTIONS.filter(
  (option) => option.value !== "room_information",
);

const TABLE_TARGET_BEHAVIOR_OPTIONS = [{ value: "general", label: "عامة" }];

const BEHAVIOR_OPTIONS_BY_TARGET_TYPE = {
  all: ALL_TARGET_BEHAVIOR_OPTIONS,
  // Room info needs a tournament; a squad card has none.
  squad: ALL_TARGET_BEHAVIOR_OPTIONS,
  table: TABLE_TARGET_BEHAVIOR_OPTIONS,
};

const notificationBehaviorMap = {
  general: {
    getExtraData: () => ({}),
  },
  scroll_to_tournament: {
    getExtraData: ({ selectedTournament, contextTournamentId }) => ({
      kind: "scroll_to_tournament",
      tournamentId: selectedTournament || contextTournamentId || null,
    }),
  },
  open_link: {
    getExtraData: ({ toOpenLink }) => ({
      kind: "open_link",
      link: toOpenLink,
    }),
  },
  room_information: {
    getExtraData: ({
      gameRoomInfo,
      contextTournamentId,
      contextTournamentName,
    }) => {
      const normalizedTournamentName = String(
        contextTournamentName || "",
      ).trim();
      const roomInfo = {
        ...(gameRoomInfo || {}),
      };

      if (normalizedTournamentName && !roomInfo.tournamentName) {
        roomInfo.tournamentName = normalizedTournamentName;
      }

      return {
        kind: "room_information",
        roomInfo,
        ...(contextTournamentId ? { tournamentId: contextTournamentId } : {}),
        ...(normalizedTournamentName
          ? { tournamentName: normalizedTournamentName }
          : {}),
      };
    },
  },
};

const SendNotification = ({
  targetType,
  type,
  targetId,
  contextTournamentId = "",
  contextTournamentName = "",
  iconOnly = false,
  tooltip = "ارسال اشعار",
  className = "",
  iconClassName = "",
}) => {
  const [title, setTitle] = useState("");
  const [enTitle, setEnTitle] = useState("");

  const [body, setBody] = useState("");
  const [enBody, setEnBody] = useState("");

  const [behaviorType, setBehaviorType] = useState("general");

  const [selectedTournament, setSelectedTournament] = useState("");
  const [toOpenLink, setToOpenLink] = useState("");
  const [gameRoomInfo, setGameRoomInfo] = useState({
    roomId: "",
    roomPass: "",
    roomName: "",
  });

  const [showNotification, setShowNotification] = useState(false);

  const dispatch = useDispatch();

  const { sendStatus } = useSelector((state) => state.notification);
  const { list: tournaments, fetchStatus } = useSelector(
    (state) => state.tournaments,
  );
  const behaviorOptions =
    BEHAVIOR_OPTIONS_BY_TARGET_TYPE[targetType] || DEFAULT_BEHAVIOR_OPTIONS;

  const clearAllFieldsAndCloseModal = () => {
    setTitle("");
    setEnTitle("");
    setBody("");
    setEnBody("");
    setBehaviorType("general");
    setToOpenLink("");
    setSelectedTournament("");
    setGameRoomInfo({ roomId: "", roomPass: "", roomName: "" });
    setShowNotification(false);
  };

  const handleSend = () => {
    const behavior =
      notificationBehaviorMap[behaviorType] || notificationBehaviorMap.general;

    if (
      behaviorType === "scroll_to_tournament" &&
      !selectedTournament &&
      !contextTournamentId
    ) {
      dispatch(
        showModal({
          message: "يرجى اختيار بطولة عند استخدام الانتقال الى البطولة",
          status: "error",
        }),
      );
      return;
    }

    if (behaviorType === "open_link" && toOpenLink.trim() === "") {
      dispatch(
        showModal({
          message: "يرجى إدخال رابط صالح",
          status: "error",
        }),
      );
      return;
    }

    if (
      sendStatus !== "loading" &&
      title.trim() !== "" &&
      body.trim() !== "" &&
      enTitle.trim() !== "" &&
      enBody.trim() !== ""
    ) {
      const behaviorContext = {
        selectedTournament,
        toOpenLink,
        gameRoomInfo,
        contextTournamentId,
        contextTournamentName,
      };

      dispatch(
        sendNotification({
          title,
          enTitle,
          body,
          enBody,
          type,
          targetType,
          targetId,
          extraData: behavior.getExtraData(behaviorContext),
        }),
      );
      clearAllFieldsAndCloseModal();
    }
  };

  useEffect(() => {
    const isCurrentBehaviorAllowed = behaviorOptions.some(
      (option) => option.value === behaviorType,
    );
    if (!isCurrentBehaviorAllowed) {
      setBehaviorType("general");
    }
  }, [behaviorOptions, behaviorType]);

  useEffect(() => {
    if (
      fetchStatus === "idle" &&
      tournaments?.length === 0 &&
      behaviorType === "scroll_to_tournament"
    ) {
      dispatch(fetchPaginatedTournament({ page: 1 }));
    }
  }, [fetchStatus, tournaments, behaviorType, dispatch]);

  return (
    <div className={styles.outerContainer}>
      <div
        className={`${styles.container} ${iconOnly ? styles.iconOnly : ""} ${className}`}
        onClick={() => setShowNotification(true)}
        title={tooltip}
      >
        <NotificationIcon
          className={`${styles.notificationIcon} ${iconClassName}`}
        />
        {!iconOnly && (
          <TextWrapper textStyle="textXSmall">ارسال اشعار</TextWrapper>
        )}
      </div>
      {showNotification && (
        <Dialog
          title="ارسال اشعار"
          icon={<NotificationIcon />}
          tone="info"
          size="md"
          onClose={clearAllFieldsAndCloseModal}
          footer={
            <>
              <DialogButton
                variant="secondary"
                onClick={clearAllFieldsAndCloseModal}
              >
                إلغاء
              </DialogButton>
              <DialogButton
                variant="primary"
                onClick={handleSend}
                disabled={sendStatus === "loading"}
              >
                {sendStatus === "loading" ? "جارٍ الإرسال…" : "إرسال الإشعار"}
              </DialogButton>
            </>
          }
        >
          <div className={styles.formWrapper}>
            <SelectInput
              wrapperClassName={styles.fullSelect}
              placeHolder={"نوع الاشعار"}
              value={behaviorType}
              setValue={setBehaviorType}
              noPlaceHolder={true}
            >
              {behaviorOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>

            {behaviorType === "scroll_to_tournament" && (
              <SelectInput
                wrapperClassName={styles.fullSelect}
                placeHolder={"اختيار البطولة"}
                value={selectedTournament}
                setValue={setSelectedTournament}
              >
                {tournaments?.map((tournament) => {
                  return (
                    <option value={tournament?._id} key={tournament?._id}>
                      {tournament?.name}
                    </option>
                  );
                })}
              </SelectInput>
            )}

            {behaviorType === "open_link" && (
              <TextInput
                height="50px"
                width="100%"
                borderRadius="10px"
                bordered
                placeHolder="الرابط"
                bgColor={"grey"}
                value={toOpenLink}
                onChange={(e) => setToOpenLink(e.target.value)}
              />
            )}

            {behaviorType === "room_information" && (
              <>
                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="10px"
                  bordered
                  placeHolder="اسم الروم"
                  bgColor={"grey"}
                  value={gameRoomInfo?.roomName}
                  onChange={(e) =>
                    setGameRoomInfo((prev) => {
                      return { ...prev, roomName: e.target.value };
                    })
                  }
                />
                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="10px"
                  bordered
                  placeHolder="ايدي الروم"
                  bgColor={"grey"}
                  value={gameRoomInfo?.roomId}
                  onChange={(e) =>
                    setGameRoomInfo((prev) => {
                      return { ...prev, roomId: e.target.value };
                    })
                  }
                />
                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="10px"
                  bordered
                  placeHolder="باسوورد الروم"
                  bgColor={"grey"}
                  value={gameRoomInfo?.roomPass}
                  onChange={(e) =>
                    setGameRoomInfo((prev) => {
                      return { ...prev, roomPass: e.target.value };
                    })
                  }
                />
              </>
            )}

            <div className={styles.horizontalLine}></div>

            <TextInput
              height="50px"
              width="100%"
              borderRadius="10px"
              bordered
              placeHolder="عنوان الاشعار"
              bgColor={"grey"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeHolder="محتوى الاشعار"
            />

            <div className={styles.horizontalLine}></div>

            <TextInput
              height="50px"
              width="100%"
              borderRadius="10px"
              bordered
              placeHolder="عنوان الاشعار بالانجليزية"
              bgColor={"grey"}
              value={enTitle}
              onChange={(e) => setEnTitle(e.target.value)}
            />

            <TextArea
              value={enBody}
              onChange={(e) => setEnBody(e.target.value)}
              placeHolder="محتوى الاشعار بالانجليزية"
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default SendNotification;
